import { useEffect, useRef, useState } from 'react';

// The one piece of ambient motion left on the site (Chase 2026-07-22): a
// whisper-faint radial glow that trails the cursor. Deliberately minimal —
// a single soft violet blob, low opacity, no particles, no WebGL, no deps.
//
// Desktop-only by design: it mounts only when the device has a fine pointer
// AND real hover (matchMedia), so phones and tablets get nothing at all —
// no background/cursor effect on touch, per Chase's ask. Also bows out for
// reduced-motion / the site motion toggle (passed in as `enabled`).
export default function CursorGlow({ enabled = true }) {
  const ref = useRef(null);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!isDesktop || !enabled) return;
    let raf = 0;
    const move = (e) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = ref.current;
        if (el) {
          el.style.transform = `translate3d(${e.clientX - 300}px, ${e.clientY - 300}px, 0)`;
          el.style.opacity = '1';
        }
      });
    };
    const leave = () => { if (ref.current) ref.current.style.opacity = '0'; };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerleave', leave);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerleave', leave);
    };
  }, [isDesktop, enabled]);

  if (!isDesktop || !enabled) return null;

  return (
    <div
      ref={ref}
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: 600,
        height: 600,
        borderRadius: '50%',
        pointerEvents: 'none',
        zIndex: 0,
        opacity: 0,
        transition: 'opacity 0.4s ease',
        background: 'radial-gradient(circle, rgba(82,39,255,0.06) 0%, rgba(82,39,255,0) 62%)',
      }}
    />
  );
}
